import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { take, distinctUntilChanged, mergeMap, takeUntil } from 'rxjs/operators';
import { ItineraryService } from '../../../services/aws/itinerary/itinerary.service';
import { Select } from '@ngxs/store';
import { JourneyState } from '../journey.state';
import { IProgram } from 'src/app/models/IProgram';
import { IFlight, IFlightDetail, ILegs } from 'src/app/models/IFlight';

@Component({
    selector: 'journey-flight',
    templateUrl: './flight.component.html',
    styles: [],
    standalone: false
})
export class FlightComponent implements OnInit, OnDestroy {

    public destroy$: Subject<boolean> = new Subject<boolean>();

    preRegister: boolean = false;

    public showGlobalSpinner: boolean = true;

    public displayFlightInformation: boolean = false;

    flightOneLegs: ILegs & IFlight;
    flightTwoLegs: ILegs & IFlight;
    showTravelTips: boolean = false;
    public flightSummaries: Array<ILegs & IFlight> = [];
    @Select(JourneyState.getSelectedProgram) itineraryProgram$: Observable<IProgram>;

    constructor(private itinerary: ItineraryService) { }

    ngOnInit() {
        if (this.noDataAvailable()) {
            this.preRegister = true;
        } else {
            this.itineraryProgram$
                .pipe(
                    distinctUntilChanged(),
                    mergeMap((itineraryProgram) => this.itinerary.getFlight(itineraryProgram?.id)),
                    takeUntil(this.destroy$)
                )
                .subscribe(
                    (data: IFlight[]) => {
                        this.showGlobalSpinner = false;
                        this.flightOneLegs = null;
                        this.flightTwoLegs = null;
                        this.flightSummaries = [];
                        // Keep track of processed flightNumbers so duplicates aren’t added
                        const processed = new Set<number | string>();
                        for (const booking of (data ?? [])) {
                            const flights = booking?.flights ?? [];
                            for (const flight of flights) {
                                const key = flight?.flightNumber ?? '';
                                if (!key || processed.has(key)) continue; // skip invalid or duplicate flights
                                const legs = flight?.legs ?? [];
                                if (!legs.length) continue; // skip if no legs available
                                // Pick first and last leg to determine start and end info
                                const firstLeg = legs[0];
                                const lastLeg = legs[legs.length - 1];
                                // Create a fresh summary object combining flight + leg info
                                const summary: ILegs & IFlight = {
                                    ...(firstLeg as any), // copy base leg info
                                    endTime: lastLeg?.arrivalTime, // override end time
                                    endAirport: lastLeg?.endAirport, // override destination
                                    flightNo: +(firstLeg?.flightNo as any) || +(flight?.flightNumber as any),
                                    bookingId: booking?.bookingId,
                                    ticketNumber: booking?.ticketNumber,
                                };
                                // Add the processed summary to our list
                                this.flightSummaries.push(summary);
                                processed.add(key);
                            }
                        }
                        this.flightOneLegs = this.flightSummaries[0] ?? null;
                        this.flightTwoLegs = this.flightSummaries[1] ?? null;
                        this.displayFlightInformation = this.flightSummaries.length > 0;
                        this.preRegister = !this.displayFlightInformation;
                    },
                    () => {
                        this.showGlobalSpinner = false;
                        this.displayFlightInformation = false;
                        this.preRegister = true;
                    }
                );
        }
    }

    noDataAvailable(): boolean {
        let isNoDataAvailable = false;
        this.itineraryProgram$.pipe(take(1)).subscribe(
            (data) => {
                isNoDataAvailable = !data;
            });
        return isNoDataAvailable;
    }

    public ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
}